import React, { useEffect, useState, Fragment, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bus, Users, GraduationCap, Heart, CreditCard, CalendarClock, ShieldCheck, FileText, CheckCircle2, ArrowRight, ArrowLeft, ChevronDown, ChevronRight, Sparkles, Building2, UserCircle, Award, Lock, Receipt, Phone, Mail, Info, Quote, MapPin, Send, CircleDollarSign, Loader2, Crown } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Shared visual primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-6963">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-6964">
    <Eyebrow data-id="element-6965">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-6966">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-6967">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-6968" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-6969">
        {intro}
      </p>}
  </div>;
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-amber-400/40 pointer-events-none" data-id="element-6970" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-amber-400/40 pointer-events-none" data-id="element-6971" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-amber-400/40 pointer-events-none" data-id="element-6972" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-amber-400/40 pointer-events-none" data-id="element-6973" />
  </>;
// ---------- Types ----------
type SponsorshipType = 'full' | 'shared' | 'flexible' | '';
type PaymentPlan = 'full' | 'installments' | '';
type RecognitionPref = 'anonymous' | 'public' | 'family' | 'organization' | 'discuss' | '';
type CheckoutStep = 'type' | 'payment' | 'details' | 'review' | 'processing' | 'confirmed';
// =====================================================================
// HERO
// =====================================================================
const Hero = ({
  scrollToCheckout,
  scrollToOptions,
  scrollToTax
}: {
  scrollToCheckout: () => void;
  scrollToOptions: () => void;
  scrollToTax: () => void;
}) => <section className="relative w-full min-h-[90vh] bg-black flex items-center overflow-hidden pt-20" data-id="element-6974">
    <div className="absolute inset-0" data-id="element-6975">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.15),_transparent_60%)] pointer-events-none" data-id="element-6976" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(139,0,0,0.1),_transparent_60%)] pointer-events-none" data-id="element-6977" />
    </div>

    <div className="relative z-10 container mx-auto px-4 py-16" data-id="element-6978">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-7xl mx-auto" data-id="element-6979">
        {/* Left */}
        <div className="lg:col-span-7" data-id="element-6980">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 mb-7 bg-black/40 backdrop-blur-sm" data-id="element-6981">
            <Bus className="w-3.5 h-3.5 text-amber-400" data-id="element-6982" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-6983">
              Sponsor a Bus · Student Access Program
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-serif text-5xl sm:text-6xl md:text-7xl text-white leading-[1.02] tracking-tight mb-6" data-id="element-6984">
            Sponsor a <span className="text-amber-400" data-id="element-6985">Bus</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.25
        }} className="text-xl sm:text-2xl text-white/75 font-light leading-relaxed mb-5 max-w-2xl" data-id="element-6986">
            Help bring students into Life of the Sikh Kingdoms through sponsored
            access and school transportation support.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.35
        }} className="text-base text-white/55 font-light leading-relaxed mb-9 max-w-xl" data-id="element-6987">
            Your sponsorship helps middle school and high school students
            experience Sikh history in a vivid, memorable, and meaningful way.
            Sponsors can fund a full bus, contribute toward a shared
            sponsorship, and choose full payment or installment options.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.45
        }} className="flex flex-col sm:flex-row items-start gap-3" data-id="element-6988">
            <button onClick={scrollToCheckout} className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-6989">
              <Bus className="w-4 h-4" data-id="element-6990" />
              Sponsor a Bus
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6991" />
            </button>
            <button onClick={scrollToOptions} className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-6992">
              View Sponsorship Options
            </button>
          </motion.div>

          <motion.button onClick={scrollToTax} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.7,
          delay: 0.7
        }} className="mt-6 inline-flex items-center gap-2 text-white/45 hover:text-amber-400 text-xs tracking-wide transition-colors group" data-id="element-6993">
            <Receipt className="w-3 h-3" data-id="element-6994" />
            How Tax Receipts Work
            <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" data-id="element-6995" />
          </motion.button>
        </div>

        {/* Right — impact callout */}
        <motion.div initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.9,
        delay: 0.4
      }} className="lg:col-span-5" data-id="element-6996">
          <div className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-8" data-id="element-6997">
            <CornerOrnaments data-id="element-6998" />
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-6999" />
            <Eyebrow data-id="element-7000">One Sponsored Bus</Eyebrow>
            <p className="font-serif text-6xl text-amber-400 mt-4 leading-none" data-id="element-7001">
              ~50
            </p>
            <p className="text-white/55 text-sm mt-1 font-light" data-id="element-7002">
              students reached per bus
            </p>

            <div className="mt-7 space-y-3 text-sm" data-id="element-7003">
              {['Round-trip transportation', 'Student access tickets', 'School participation support', 'Educator coordination'].map(item => <div key={item} className="flex items-center gap-3" data-id="element-7004">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" data-id="element-7005" />
                  <span className="text-white/75 font-light" data-id="element-7006">{item}</span>
                </div>)}
            </div>

            <div className="mt-7 pt-6 border-t border-amber-400/15" data-id="element-7007">
              <p className="text-white/40 text-xs font-light leading-relaxed flex items-start gap-2" data-id="element-7008">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-7009" />
                Tax receipts, where applicable, are issued through
                <strong className="text-white/65 font-normal mx-1" data-id="element-7010">
                  Sikh Heritage Museum of Canada
                </strong>
                .
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// WHAT IT FUNDS
// =====================================================================
const WhatItFunds = () => {
  const items = [{
    icon: Bus,
    title: 'Student Transportation',
    body: 'Round-trip charter buses from schools to the experience and back.'
  }, {
    icon: GraduationCap,
    title: 'Access Tickets',
    body: 'Full immersive experience tickets covered for each student on the bus.'
  }, {
    icon: Building2,
    title: 'School Participation',
    body: 'Administrative coordination and educator support for participating schools.'
  }, {
    icon: Users,
    title: 'Middle &amp; High School Groups',
    body: 'Designed for grades 6–12. Reaches students who might not otherwise attend.'
  }];
  return <section className="relative py-24 md:py-28 bg-black border-t border-amber-400/10" data-id="element-7011">
      <div className="container mx-auto px-4" data-id="element-7012">
        <SectionHeading eyebrow="What Sponsorship Funds" title="A Real Bus, A Real" highlight="Group of Students" intro="Each sponsored bus supports approximately 50 students from a partner school — transportation, tickets, and on-the-day support." data-id="element-7013" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14 max-w-6xl mx-auto" data-id="element-7014">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors bg-gradient-to-b from-amber-950/10 to-transparent p-6" data-id="element-7015">
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-7016" />
              <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-7017">
                <it.icon className="w-4 h-4 text-amber-400" data-id="element-7018" />
              </div>
              <h3 className="font-serif text-lg text-white leading-tight mb-2.5" dangerouslySetInnerHTML={{
            __html: it.title
          }} data-id="element-7019" />
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-7020">
                {it.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// SPONSORSHIP OPTIONS
// =====================================================================
const SponsorshipOptions = ({
  onChoose
}: {
  onChoose: (t: SponsorshipType) => void;
}) => {
  const options = [{
    id: 'full' as const,
    icon: Bus,
    tag: 'Full Sponsorship',
    title: 'Full Bus Sponsor',
    amount: '$5,000',
    blurb: 'Fund an entire bus and help bring a full student group to the experience.',
    supports: ['~50 students transported', 'Full ticket access included', 'Acknowledgment as a Full Bus Sponsor'],
    featured: true
  }, {
    id: 'shared' as const,
    icon: Users,
    tag: 'Shared',
    title: 'Shared Bus Sponsor',
    amount: '$1,000+',
    blurb: 'Contribute toward a bus together with other sponsors.',
    supports: ['Pooled with other contributors', 'Bus completion tracking', 'Shared sponsor recognition']
  }, {
    id: 'flexible' as const,
    icon: Heart,
    tag: 'Flexible',
    title: 'Flexible Supporter',
    amount: 'Choose Amount',
    blurb: 'Choose your own contribution amount toward student transportation and access.',
    supports: ['Any amount from $100', 'Direct student access support', 'Optional acknowledgment']
  }];
  return <section id="sponsorship-options" className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10 scroll-mt-20" data-id="element-7021">
      <div className="container mx-auto px-4" data-id="element-7022">
        <SectionHeading eyebrow="Sponsorship Options" title="Choose How You" highlight="Sponsor" intro="Three structured ways to support student access. Each is real, transparent, and operationally clear." data-id="element-7023" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-14 max-w-6xl mx-auto" data-id="element-7024">
          {options.map((opt, i) => <motion.div key={opt.id} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className={`relative border bg-gradient-to-b from-amber-950/15 to-black p-7 flex flex-col transition-colors ${opt.featured ? 'border-amber-400/50 hover:border-amber-400 shadow-[0_0_30px_rgba(212,175,55,0.08)]' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7025">
              <span className={`absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent ${opt.featured ? 'via-amber-400' : 'via-amber-400/50'} to-transparent`} data-id="element-7026" />
              {opt.featured && <span className="absolute -top-3 right-6 text-[9px] tracking-[0.3em] uppercase text-black bg-amber-400 px-2 py-1 font-bold" data-id="element-7027">
                  Most Impact
                </span>}
              <div className="flex items-center justify-between mb-5" data-id="element-7028">
                <div className="w-11 h-11 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-7029">
                  <opt.icon className="w-5 h-5 text-amber-400" strokeWidth={1.25} data-id="element-7030" />
                </div>
                <span className="text-[9px] tracking-[0.3em] uppercase text-amber-400/70 font-light" data-id="element-7031">
                  {opt.tag}
                </span>
              </div>
              <h3 className="font-serif text-2xl text-white mb-2 leading-tight" data-id="element-7032">
                {opt.title}
              </h3>
              <p className="font-serif text-3xl text-amber-400 mb-1 leading-tight" data-id="element-7033">
                {opt.amount}
              </p>
              <p className="text-white/45 text-[10px] tracking-[0.25em] uppercase mb-5 font-light" data-id="element-7034">
                {opt.id === 'flexible' ? 'Suggested $100+' : 'Suggested amount'}
              </p>
              <p className="text-white/65 text-sm font-light leading-relaxed mb-5" data-id="element-7035">
                {opt.blurb}
              </p>
              <ul className="space-y-2.5 mb-7 flex-1" data-id="element-7036">
                {opt.supports.map(s => <li key={s} className="flex items-start gap-2.5" data-id="element-7037">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" data-id="element-7038" />
                    <span className="text-white/70 text-sm font-light leading-relaxed" data-id="element-7039">
                      {s}
                    </span>
                  </li>)}
              </ul>
              <button onClick={() => onChoose(opt.id)} className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors ${opt.featured ? 'bg-amber-400 text-black hover:bg-amber-300 shadow-[0_0_25px_rgba(212,175,55,0.25)]' : 'border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 hover:border-amber-400'}`} data-id="element-7040">
                Sponsor This
                <ArrowRight className="w-3 h-3" data-id="element-7041" />
              </button>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// PAYMENT OPTIONS EXPLAINER
// =====================================================================
const PaymentOptions = () => <section className="relative py-24 md:py-28 bg-black border-t border-amber-400/10" data-id="element-7042">
    <div className="container mx-auto px-4" data-id="element-7043">
      <SectionHeading eyebrow="Payment Flexibility" title="Pay in Full or in" highlight="Installments" intro="Sponsors can complete their support in one payment or choose a structured installment plan through Stripe." data-id="element-7044" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto mt-14" data-id="element-7045">
        {[{
        icon: CreditCard,
        tag: 'Option 1',
        title: 'One-Time Payment',
        body: 'Complete your sponsorship in full today through Stripe checkout. Secure, instant, with email confirmation.',
        chip: 'Processed instantly'
      }, {
        icon: CalendarClock,
        tag: 'Option 2',
        title: 'Installment Plan',
        body: 'Spread your sponsorship over scheduled payments while securing your commitment now. Installment sponsors still complete a formal commitment flow.',
        chip: 'Stripe scheduled payments'
      }].map((p, i) => <motion.div key={p.title} initial={{
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: i * 0.08
      }} className="relative border border-amber-400/20 hover:border-amber-400/50 transition-colors bg-gradient-to-b from-amber-950/15 to-black p-7" data-id="element-7046">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-7047" />
            <div className="flex items-center justify-between mb-5" data-id="element-7048">
              <div className="w-12 h-12 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-7049">
                <p.icon className="w-5 h-5 text-amber-400" strokeWidth={1.25} data-id="element-7050" />
              </div>
              <span className="text-[9px] tracking-[0.3em] uppercase text-amber-400/70 font-light" data-id="element-7051">
                {p.tag}
              </span>
            </div>
            <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-7052">
              {p.title}
            </h3>
            <p className="text-white/65 text-sm font-light leading-relaxed mb-5" data-id="element-7053">
              {p.body}
            </p>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/25 text-amber-400/85 text-[10px] tracking-[0.25em] uppercase" data-id="element-7054">
              <Lock className="w-2.5 h-2.5" data-id="element-7055" />
              {p.chip}
            </div>
          </motion.div>)}
      </div>
    </div>
  </section>;
// =====================================================================
// TAX RECEIPT EXPLAINER
// =====================================================================
const TaxReceipt = () => {
  const steps = [{
    n: '01',
    title: 'Indicate Receipt Request',
    body: 'Tell us during sponsorship if you would like a tax receipt issued.'
  }, {
    n: '02',
    title: 'Complete Required Details',
    body: 'Provide the legal name and address required for receipt issuance.'
  }, {
    n: '03',
    title: 'Processed by the Museum',
    body: 'Sikh Heritage Museum of Canada handles receipt processing and issuance.'
  }];
  return <section id="tax-receipt" className="relative py-24 md:py-28 bg-[#080808] border-t border-amber-400/10 scroll-mt-20" data-id="element-7056">
      <div className="container mx-auto px-4" data-id="element-7057">
        <SectionHeading eyebrow="Tax Receipt" title="How Tax Receipts" highlight="Are Issued" intro="Tax receipts, where applicable, are issued through Sikh Heritage Museum of Canada — not directly through Sikh Kingdoms." data-id="element-7058" />

        <div className="max-w-4xl mx-auto mt-14" data-id="element-7059">
          {/* 3 steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-id="element-7060">
            {steps.map((s, i) => <motion.div key={s.n} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="relative border border-amber-400/20 bg-gradient-to-b from-amber-950/10 to-transparent p-6" data-id="element-7061">
                <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-7062" />
                <p className="font-serif text-amber-400/50 text-3xl leading-none mb-3" data-id="element-7063">
                  {s.n}
                </p>
                <h4 className="font-serif text-lg text-white leading-tight mb-2" data-id="element-7064">
                  {s.title}
                </h4>
                <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-7065">
                  {s.body}
                </p>
              </motion.div>)}
          </div>

          {/* Important callout */}
          <div className="border border-amber-400/25 bg-black/40 p-5 sm:p-6 flex items-start gap-4" data-id="element-7066">
            <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-7067" />
            <div className="flex-1" data-id="element-7068">
              <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-3" data-id="element-7069">
                Important
              </p>
              <ul className="space-y-1.5 text-white/65 text-xs sm:text-sm leading-relaxed font-light" data-id="element-7070">
                <li data-id="element-7071">
                  Sponsorship support may be eligible for a tax receipt where
                  applicable.
                </li>
                <li data-id="element-7072">
                  Tax receipts are issued by Sikh Heritage Museum of Canada.
                </li>
                <li data-id="element-7073">
                  Sponsors who request a receipt must provide required
                  information.
                </li>
                <li data-id="element-7074">
                  Receipt timing and issuance is handled through the museum
                  workflow.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// CHECKOUT FLOW — the heart of the page
// =====================================================================
type FormState = {
  sponsorshipType: SponsorshipType;
  amount: number;
  paymentPlan: PaymentPlan;
  installments: number;
  fullName: string;
  orgName: string;
  email: string;
  phone: string;
  taxReceipt: 'yes' | 'no' | '';
  legalName: string;
  mailingAddress: string;
  recognitionPref: RecognitionPref;
  notes: string;
  largerInterest: boolean;
};
const initialForm: FormState = {
  sponsorshipType: '',
  amount: 0,
  paymentPlan: '',
  installments: 3,
  fullName: '',
  orgName: '',
  email: '',
  phone: '',
  taxReceipt: '',
  legalName: '',
  mailingAddress: '',
  recognitionPref: '',
  notes: '',
  largerInterest: false
};
const Checkout = ({
  initialType,
  onConfirmed
}: {
  initialType: SponsorshipType;
  onConfirmed: (s: FormState) => void;
}) => {
  const [step, setStep] = useState<CheckoutStep>('type');
  const [form, setForm] = useState<FormState>(initialForm);
  // If parent passed a pre-selected type, jump forward
  useEffect(() => {
    if (initialType) {
      const defaultAmount = initialType === 'full' ? 5000 : initialType === 'shared' ? 1000 : 250;
      setForm(f => ({
        ...f,
        sponsorshipType: initialType,
        amount: defaultAmount
      }));
      setStep('payment');
    }
  }, [initialType]);
  const update = <K extends keyof FormState,>(k: K, v: FormState[K]) => setForm(prev => ({
    ...prev,
    [k]: v
  }));
  const stepIndex = (['type', 'payment', 'details', 'review', 'processing', 'confirmed'] as const).indexOf(step);
  const installmentAmount = form.amount / (form.installments || 1);
  // ---------- Validation per step ----------
  const canAdvance = () => {
    if (step === 'type') return form.sponsorshipType !== '' && form.amount > 0;
    if (step === 'payment') return form.paymentPlan !== '';
    if (step === 'details') {
      const base = form.fullName.length > 1 && /.+@.+\..+/.test(form.email) && form.phone.length > 4 && form.taxReceipt !== '' && form.recognitionPref !== '';
      if (form.taxReceipt === 'yes') {
        return base && form.legalName.length > 1 && form.mailingAddress.length > 5;
      }
      return base;
    }
    return true;
  };
  const handleNext = () => {
    if (!canAdvance()) return;
    if (step === 'review') {
      setStep('processing');
      setTimeout(() => {
        setStep('confirmed');
        onConfirmed(form);
      }, 1800);
      return;
    }
    const order: CheckoutStep[] = ['type', 'payment', 'details', 'review'];
    const i = order.indexOf(step);
    if (i >= 0 && i < order.length - 1) setStep(order[i + 1]);
  };
  const handleBack = () => {
    const order: CheckoutStep[] = ['type', 'payment', 'details', 'review'];
    const i = order.indexOf(step);
    if (i > 0) setStep(order[i - 1]);
  };
  const inputBase = 'w-full bg-black/60 border border-amber-400/25 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/70 transition-colors';
  return <section id="checkout" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 scroll-mt-20 overflow-hidden" data-id="element-7075">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06),_transparent_70%)] pointer-events-none" data-id="element-7076" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-7077">
        <SectionHeading eyebrow="Begin Your Sponsorship" title="Sponsor" highlight="Checkout" intro="A clear, four-step flow. Your information is processed securely through Stripe." data-id="element-7078" />

        <div className="max-w-3xl mx-auto mt-14" data-id="element-7079">
          {/* Stepper */}
          {step !== 'processing' && step !== 'confirmed' && <div className="mb-10 flex items-center justify-between gap-2" data-id="element-7080">
              {[{
            id: 'type',
            label: 'Sponsorship'
          }, {
            id: 'payment',
            label: 'Payment'
          }, {
            id: 'details',
            label: 'Details'
          }, {
            id: 'review',
            label: 'Review'
          }].map((s, i) => {
            const active = step === s.id;
            const past = stepIndex > i;
            return <Fragment key={s.id} data-id="element-7081">
                    <div className="flex flex-col items-center gap-2 flex-1" data-id="element-7082">
                      <div className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-bold transition-colors ${active ? 'bg-amber-400 border-amber-400 text-black' : past ? 'bg-amber-400/20 border-amber-400/60 text-amber-400' : 'border-amber-400/20 text-amber-400/40'}`} data-id="element-7083">
                        {past ? <CheckCircle2 className="w-4 h-4" data-id="element-7084" /> : i + 1}
                      </div>
                      <p className={`text-[9px] tracking-[0.25em] uppercase ${active ? 'text-amber-400 font-bold' : 'text-white/40 font-light'}`} data-id="element-7085">
                        {s.label}
                      </p>
                    </div>
                    {i < 3 && <div className={`flex-shrink-0 h-px w-6 sm:w-12 transition-colors -mt-6 ${past ? 'bg-amber-400/60' : 'bg-amber-400/15'}`} data-id="element-7086" />}
                  </Fragment>;
          })}
            </div>}

          {/* Card */}
          <div className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/8 to-black p-7 sm:p-10" data-id="element-7087">
            <CornerOrnaments data-id="element-7088" />
            <span className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-7089" />

            <AnimatePresence mode="wait" data-id="element-7090">
              {/* STEP 1: TYPE */}
              {step === 'type' && <motion.div key="type" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} data-id="element-7091">
                  <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7092">
                    Choose Sponsorship Type
                  </h3>
                  <p className="text-white/55 text-sm font-light mb-7" data-id="element-7093">
                    Pick how you would like to support student access.
                  </p>
                  <div className="space-y-3 mb-6" data-id="element-7094">
                    {[{
                  id: 'full' as const,
                  label: 'Full Bus Sponsor',
                  sub: 'Fund an entire bus · ~50 students',
                  defaultAmt: 5000
                }, {
                  id: 'shared' as const,
                  label: 'Shared Bus Sponsor',
                  sub: 'Contribute toward a shared bus',
                  defaultAmt: 1000
                }, {
                  id: 'flexible' as const,
                  label: 'Flexible Supporter',
                  sub: 'Any amount from $100',
                  defaultAmt: 250
                }].map(opt => {
                  const active = form.sponsorshipType === opt.id;
                  return <button key={opt.id} type="button" onClick={() => {
                    update('sponsorshipType', opt.id);
                    update('amount', opt.defaultAmt);
                  }} className={`w-full flex items-center gap-4 border px-5 py-4 text-left transition-colors ${active ? 'border-amber-400/70 bg-amber-400/5' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7095">
                          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${active ? 'border-amber-400 bg-amber-400/30' : 'border-amber-400/40'}`} data-id="element-7096">
                            {active && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-7097" />}
                          </span>
                          <div className="flex-1" data-id="element-7098">
                            <p className="font-serif text-white text-base leading-tight" data-id="element-7099">
                              {opt.label}
                            </p>
                            <p className="text-white/45 text-xs font-light mt-0.5" data-id="element-7100">
                              {opt.sub}
                            </p>
                          </div>
                          <span className="text-amber-400 font-serif text-base shrink-0" data-id="element-7101">
                            ${opt.defaultAmt.toLocaleString()}
                          </span>
                        </button>;
                })}
                  </div>

                  {/* Custom amount */}
                  {form.sponsorshipType && <div className="border-t border-amber-400/15 pt-5" data-id="element-7102">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7103">
                        Adjust Amount (USD)
                      </label>
                      <div className="relative" data-id="element-7104">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400 font-serif text-lg" data-id="element-7105">
                          $
                        </span>
                        <input type="number" min="100" step="50" value={form.amount || ''} onChange={e => update('amount', Number(e.target.value))} className={`${inputBase} pl-10 font-mono text-lg`} data-id="element-7106" />
                      </div>
                      <p className="text-white/40 text-[10px] mt-1.5 font-light" data-id="element-7107">
                        Minimum $100. Adjust based on what feels right for your
                        sponsorship.
                      </p>
                    </div>}
                </motion.div>}

              {/* STEP 2: PAYMENT */}
              {step === 'payment' && <motion.div key="payment" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} data-id="element-7108">
                  <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7109">
                    Payment Plan
                  </h3>
                  <p className="text-white/55 text-sm font-light mb-7" data-id="element-7110">
                    Complete your{' '}
                    <span className="text-amber-400" data-id="element-7111">
                      ${form.amount.toLocaleString()}
                    </span>{' '}
                    sponsorship today, or schedule it over installments.
                  </p>

                  <div className="space-y-3" data-id="element-7112">
                    {[{
                  id: 'full' as const,
                  icon: CreditCard,
                  label: 'One-Time Payment',
                  sub: `One charge of $${form.amount.toLocaleString()} today`
                }, {
                  id: 'installments' as const,
                  icon: CalendarClock,
                  label: 'Installment Plan',
                  sub: 'Scheduled monthly payments'
                }].map(opt => {
                  const active = form.paymentPlan === opt.id;
                  return <button key={opt.id} type="button" onClick={() => update('paymentPlan', opt.id)} className={`w-full flex items-start gap-4 border px-5 py-5 text-left transition-colors ${active ? 'border-amber-400/70 bg-amber-400/5' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7113">
                          <div className={`w-10 h-10 border flex items-center justify-center shrink-0 ${active ? 'border-amber-400 bg-amber-400/15' : 'border-amber-400/30 bg-black/40'}`} data-id="element-7114">
                            <opt.icon className="w-4 h-4 text-amber-400" data-id="element-7115" />
                          </div>
                          <div className="flex-1" data-id="element-7116">
                            <p className="font-serif text-white text-base leading-tight" data-id="element-7117">
                              {opt.label}
                            </p>
                            <p className="text-white/45 text-xs font-light mt-1" data-id="element-7118">
                              {opt.sub}
                            </p>
                          </div>
                          {active && <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" data-id="element-7119" />}
                        </button>;
                })}
                  </div>

                  {form.paymentPlan === 'installments' && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-5 border border-amber-400/20 bg-amber-950/8 p-5" data-id="element-7120">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-3 font-light" data-id="element-7121">
                        Installment Schedule
                      </label>
                      <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-7122">
                        {[3, 6, 12].map(months => {
                    const active = form.installments === months;
                    return <button type="button" key={months} onClick={() => update('installments', months)} className={`px-3 py-3 text-xs tracking-wide border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-bold' : 'border-amber-400/25 text-white/70 hover:border-amber-400/55'}`} data-id="element-7123">
                              {months} months
                            </button>;
                  })}
                      </div>
                      <div className="flex items-baseline justify-between border-t border-amber-400/15 pt-3" data-id="element-7124">
                        <span className="text-white/55 text-xs font-light" data-id="element-7125">
                          Per month
                        </span>
                        <span className="font-serif text-amber-400 text-2xl" data-id="element-7126">
                          ${installmentAmount.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-white/40 text-[10px] mt-2 font-light flex items-center gap-1.5" data-id="element-7127">
                        <Lock className="w-2.5 h-2.5" data-id="element-7128" />
                        Processed automatically through Stripe each month
                      </p>
                    </motion.div>}
                </motion.div>}

              {/* STEP 3: DETAILS */}
              {step === 'details' && <motion.div key="details" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} className="space-y-6" data-id="element-7129">
                  <div data-id="element-7130">
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7131">
                      Your Details
                    </h3>
                    <p className="text-white/55 text-sm font-light" data-id="element-7132">
                      Used for confirmation, follow-up, and tax receipt routing
                      if requested.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7133">
                    <div data-id="element-7134">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7135">
                        Full Name *
                      </label>
                      <input className={inputBase} value={form.fullName} onChange={e => update('fullName', e.target.value)} placeholder="Your full name" data-id="element-7136" />
                    </div>
                    <div data-id="element-7137">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7138">
                        Organization / Family
                      </label>
                      <input className={inputBase} value={form.orgName} onChange={e => update('orgName', e.target.value)} placeholder="Optional" data-id="element-7139" />
                    </div>
                    <div data-id="element-7140">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7141">
                        Email *
                      </label>
                      <input type="email" className={inputBase} value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@example.com" data-id="element-7142" />
                    </div>
                    <div data-id="element-7143">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7144">
                        Phone *
                      </label>
                      <input type="tel" className={inputBase} value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (___) ___-____" data-id="element-7145" />
                    </div>
                  </div>

                  {/* Tax receipt */}
                  <div className="border border-amber-400/15 bg-amber-950/8 p-5" data-id="element-7146">
                    <div className="flex items-start gap-3 mb-4" data-id="element-7147">
                      <Receipt className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-7148" />
                      <div data-id="element-7149">
                        <p className="text-white text-sm font-light leading-snug" data-id="element-7150">
                          Would you like a tax receipt issued through{' '}
                          <span className="text-amber-400" data-id="element-7151">
                            Sikh Heritage Museum of Canada
                          </span>
                          ?
                        </p>
                        <p className="text-white/40 text-[11px] mt-1 font-light" data-id="element-7152">
                          Where applicable. Receipt processing is handled by the
                          museum.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-7153">
                      {[{
                    v: 'yes',
                    label: 'Yes, request receipt'
                  }, {
                    v: 'no',
                    label: 'No, thank you'
                  }].map(opt => {
                    const active = form.taxReceipt === opt.v;
                    return <button key={opt.v} type="button" onClick={() => update('taxReceipt', opt.v as 'yes' | 'no')} className={`px-3 py-2.5 text-xs tracking-wide border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-bold' : 'border-amber-400/25 text-white/70 hover:border-amber-400/55'}`} data-id="element-7154">
                            {opt.label}
                          </button>;
                  })}
                    </div>
                    {form.taxReceipt === 'yes' && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="space-y-3 pt-3 border-t border-amber-400/15" data-id="element-7155">
                        <div data-id="element-7156">
                          <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7157">
                            Legal Name for Receipt *
                          </label>
                          <input className={inputBase} value={form.legalName} onChange={e => update('legalName', e.target.value)} placeholder="As it should appear on the receipt" data-id="element-7158" />
                        </div>
                        <div data-id="element-7159">
                          <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7160">
                            Mailing Address *
                          </label>
                          <textarea rows={2} className={`${inputBase} resize-none`} value={form.mailingAddress} onChange={e => update('mailingAddress', e.target.value)} placeholder="Street, City, Province/State, Postal Code, Country" data-id="element-7161" />
                        </div>
                      </motion.div>}
                  </div>

                  {/* Recognition */}
                  <div data-id="element-7162">
                    <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-3 font-light" data-id="element-7163">
                      Recognition Preference *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-7164">
                      {[{
                    v: 'anonymous' as const,
                    icon: Lock,
                    label: 'Anonymous'
                  }, {
                    v: 'public' as const,
                    icon: UserCircle,
                    label: 'Public Acknowledgment'
                  }, {
                    v: 'family' as const,
                    icon: Heart,
                    label: 'Family Acknowledgment'
                  }, {
                    v: 'organization' as const,
                    icon: Building2,
                    label: 'Business / Organization'
                  }, {
                    v: 'discuss' as const,
                    icon: Phone,
                    label: 'Discuss with Team'
                  }].map(opt => {
                    const active = form.recognitionPref === opt.v;
                    return <button key={opt.v} type="button" onClick={() => update('recognitionPref', opt.v)} className={`flex items-center gap-3 border px-4 py-3 text-left transition-colors ${active ? 'border-amber-400/70 bg-amber-400/5' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7165">
                            <opt.icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-amber-400' : 'text-amber-400/55'}`} data-id="element-7166" />
                            <span className="text-sm text-white/80 font-light" data-id="element-7167">
                              {opt.label}
                            </span>
                            {active && <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 ml-auto" data-id="element-7168" />}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Notes */}
                  <div data-id="element-7169">
                    <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7170">
                      Notes / Dedication (Optional)
                    </label>
                    <textarea rows={3} className={`${inputBase} resize-none`} value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Dedicate your sponsorship, share a school preference, or note anything we should know." data-id="element-7171" />
                  </div>

                  {/* Larger interest */}
                  <label className="flex items-start gap-3 cursor-pointer border border-amber-400/15 bg-black/40 p-4 hover:border-amber-400/35 transition-colors" data-id="element-7172">
                    <input type="checkbox" checked={form.largerInterest} onChange={() => update('largerInterest', !form.largerInterest)} className="sr-only" data-id="element-7173" />
                    <span className={`mt-0.5 w-4 h-4 border ${form.largerInterest ? 'border-amber-400 bg-amber-400' : 'border-amber-400/40'} flex items-center justify-center shrink-0 transition-colors`} data-id="element-7174">
                      {form.largerInterest && <CheckCircle2 className="w-3 h-3 text-black" data-id="element-7175" />}
                    </span>
                    <span className="text-white/70 text-sm font-light leading-relaxed" data-id="element-7176">
                      I would like to discuss a larger sponsorship or multiple
                      buses with the team.
                    </span>
                  </label>
                </motion.div>}

              {/* STEP 4: REVIEW */}
              {step === 'review' && <motion.div key="review" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} data-id="element-7177">
                  <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7178">
                    Review & Confirm
                  </h3>
                  <p className="text-white/55 text-sm font-light mb-7" data-id="element-7179">
                    Confirm your sponsorship details before secure Stripe
                    checkout.
                  </p>

                  <div className="space-y-4 mb-7" data-id="element-7180">
                    {[{
                  label: 'Sponsorship Type',
                  value: form.sponsorshipType === 'full' ? 'Full Bus Sponsor' : form.sponsorshipType === 'shared' ? 'Shared Bus Sponsor' : 'Flexible Supporter'
                }, {
                  label: 'Total Amount',
                  value: `$${form.amount.toLocaleString()}`,
                  highlight: true
                }, {
                  label: 'Payment Plan',
                  value: form.paymentPlan === 'full' ? 'One-time payment' : `${form.installments} monthly installments of $${installmentAmount.toFixed(2)}`
                }, {
                  label: 'Sponsor',
                  value: `${form.fullName}${form.orgName ? ` · ${form.orgName}` : ''}`
                }, {
                  label: 'Email',
                  value: form.email
                }, {
                  label: 'Tax Receipt',
                  value: form.taxReceipt === 'yes' ? `Requested · ${form.legalName}` : 'Not requested'
                }, {
                  label: 'Recognition',
                  value: form.recognitionPref === 'anonymous' ? 'Anonymous' : form.recognitionPref === 'public' ? 'Public Acknowledgment' : form.recognitionPref === 'family' ? 'Family Acknowledgment' : form.recognitionPref === 'organization' ? 'Business / Organization' : 'Discuss with team'
                }].map(row => <div key={row.label} className="flex items-start justify-between gap-4 pb-3 border-b border-amber-400/10" data-id="element-7181">
                        <span className="text-amber-400/60 text-[10px] tracking-[0.3em] uppercase font-light shrink-0" data-id="element-7182">
                          {row.label}
                        </span>
                        <span className={`text-right text-sm font-light ${row.highlight ? 'text-amber-400 font-serif text-lg' : 'text-white/80'}`} data-id="element-7183">
                          {row.value}
                        </span>
                      </div>)}
                  </div>

                  {/* Stripe panel */}
                  <div className="border border-amber-400/30 bg-amber-950/8 p-5 flex items-center gap-4" data-id="element-7184">
                    <div className="w-12 h-12 border border-amber-400/40 bg-black/60 flex items-center justify-center shrink-0" data-id="element-7185">
                      <Lock className="w-5 h-5 text-amber-400" data-id="element-7186" />
                    </div>
                    <div className="flex-1" data-id="element-7187">
                      <p className="text-white text-sm font-light leading-snug" data-id="element-7188">
                        Secure payment processed by{' '}
                        <span className="text-amber-400 font-medium" data-id="element-7189">
                          Stripe
                        </span>
                      </p>
                      <p className="text-white/45 text-xs mt-0.5 font-light" data-id="element-7190">
                        You&rsquo;ll be redirected to Stripe&rsquo;s secure
                        checkout to complete your sponsorship.
                      </p>
                    </div>
                  </div>
                </motion.div>}

              {/* PROCESSING */}
              {step === 'processing' && <motion.div key="processing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="py-14 flex flex-col items-center text-center" data-id="element-7191">
                  <Loader2 className="w-10 h-10 text-amber-400 animate-spin mb-5" data-id="element-7192" />
                  <p className="font-serif text-xl text-white mb-2" data-id="element-7193">
                    Connecting to Stripe...
                  </p>
                  <p className="text-white/45 text-sm font-light" data-id="element-7194">
                    Securing your sponsorship commitment
                  </p>
                </motion.div>}

              {/* CONFIRMED */}
              {step === 'confirmed' && <motion.div key="confirmed" initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-6" data-id="element-7195">
                  <motion.div initial={{
                scale: 0.6,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 280,
                damping: 20
              }} className="inline-flex w-16 h-16 border-2 border-amber-400 bg-amber-400/10 items-center justify-center mb-7 shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-7196">
                    <CheckCircle2 className="w-8 h-8 text-amber-400" data-id="element-7197" />
                  </motion.div>
                  <Eyebrow data-id="element-7198">Sponsorship Confirmed</Eyebrow>
                  <h3 className="font-serif text-3xl sm:text-4xl text-white mt-4 mb-4 leading-tight" data-id="element-7199">
                    Thank You,{' '}
                    <span className="text-amber-400" data-id="element-7200">
                      {form.fullName.split(' ')[0]}
                    </span>
                  </h3>
                  <p className="text-white/65 text-base font-light leading-relaxed max-w-md mx-auto mb-6" data-id="element-7201">
                    Your sponsorship of{' '}
                    <span className="text-amber-400 font-medium" data-id="element-7202">
                      ${form.amount.toLocaleString()}
                    </span>{' '}
                    has been received. A confirmation has been sent to{' '}
                    <span className="text-amber-400" data-id="element-7203">{form.email}</span>.
                  </p>
                  {form.taxReceipt === 'yes' && <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 text-amber-400 text-[10px] tracking-[0.25em] uppercase font-bold mb-3" data-id="element-7204">
                      <Receipt className="w-3 h-3" data-id="element-7205" />
                      Tax Receipt Routing Initiated
                    </div>}
                </motion.div>}
            </AnimatePresence>

            {/* Nav buttons */}
            {step !== 'processing' && step !== 'confirmed' && <div className="flex items-center justify-between gap-4 pt-7 mt-7 border-t border-amber-400/15" data-id="element-7206">
                <button type="button" onClick={handleBack} disabled={step === 'type'} className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors ${step === 'type' ? 'opacity-30 cursor-not-allowed text-white/50' : 'text-amber-400 border border-amber-400/30 hover:bg-amber-400/10'}`} data-id="element-7207">
                  <ArrowLeft className="w-3 h-3" data-id="element-7208" />
                  Back
                </button>
                <button type="button" onClick={handleNext} disabled={!canAdvance()} className={`inline-flex items-center gap-2 px-6 py-3.5 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors ${canAdvance() ? 'bg-amber-400 text-black hover:bg-amber-300 shadow-[0_0_30px_rgba(212,175,55,0.25)]' : 'bg-amber-400/15 text-black/40 cursor-not-allowed'}`} data-id="element-7209">
                  {step === 'review' ? <>
                      <Lock className="w-3.5 h-3.5" data-id="element-7210" />
                      Confirm &amp; Pay
                    </> : <>
                      Continue
                      <ArrowRight className="w-3.5 h-3.5" data-id="element-7211" />
                    </>}
                </button>
              </div>}
          </div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// WHAT HAPPENS NEXT
// =====================================================================
const WhatHappensNext = () => {
  const steps = [{
    n: '01',
    icon: CheckCircle2,
    title: 'Payment Confirmed',
    body: 'Stripe processes your payment. You receive an instant confirmation email.'
  }, {
    n: '02',
    icon: FileText,
    title: 'Sponsor Details Reviewed',
    body: 'Our team reviews your sponsorship details and recognition preference.'
  }, {
    n: '03',
    icon: Receipt,
    title: 'Tax Receipt Routed',
    body: 'If requested, your details are routed to Sikh Heritage Museum of Canada for issuance.'
  }, {
    n: '04',
    icon: Award,
    title: 'Impact &amp; Recognition',
    body: 'You receive updates on student access and your chosen acknowledgment goes live.'
  }];
  return <section className="relative py-24 md:py-28 bg-[#080808] border-t border-amber-400/10" data-id="element-7212">
      <div className="container mx-auto px-4" data-id="element-7213">
        <SectionHeading eyebrow="After Payment" title="What Happens" highlight="Next" intro="A clear four-step follow-up after your sponsorship is confirmed." data-id="element-7214" />
        <div className="max-w-6xl mx-auto mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7215">
          {steps.map((s, i) => <motion.div key={s.n} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 transition-colors bg-gradient-to-b from-amber-950/10 to-transparent p-6" data-id="element-7216">
              <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-7217" />
              <div className="flex items-center justify-between mb-4" data-id="element-7218">
                <p className="font-serif text-amber-400/50 text-3xl leading-none" data-id="element-7219">
                  {s.n}
                </p>
                <div className="w-9 h-9 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-7220">
                  <s.icon className="w-4 h-4 text-amber-400" data-id="element-7221" />
                </div>
              </div>
              <h4 className="font-serif text-lg text-white leading-tight mb-2" dangerouslySetInnerHTML={{
            __html: s.title
          }} data-id="element-7222" />
              <p className="text-white/55 text-sm font-light leading-relaxed" dangerouslySetInnerHTML={{
            __html: s.body
          }} data-id="element-7223" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// SPONSOR STATUS DASHBOARD (post-confirmation only)
// =====================================================================
const SponsorStatus = ({
  data
}: {
  data: FormState;
}) => {
  const paid = data.paymentPlan === 'full' ? data.amount : data.amount / data.installments;
  const remaining = data.paymentPlan === 'full' ? 0 : data.amount - paid;
  const progress = data.paymentPlan === 'full' ? 100 : 1 / data.installments * 100;
  return <section className="relative py-20 bg-black border-t border-amber-400/15" data-id="element-7224">
      <div className="container mx-auto px-4" data-id="element-7225">
        <SectionHeading eyebrow="Your Sponsorship" title="Live" highlight="Status" intro="A snapshot of your active sponsorship and what comes next." data-id="element-7226" />
        <div className="max-w-4xl mx-auto mt-12 relative border border-amber-400/40 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-7227">
          <CornerOrnaments data-id="element-7228" />
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-7229" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8" data-id="element-7230">
            <div data-id="element-7231">
              <Eyebrow data-id="element-7232">Pledged</Eyebrow>
              <p className="font-serif text-3xl text-white mt-2" data-id="element-7233">
                ${data.amount.toLocaleString()}
              </p>
            </div>
            <div data-id="element-7234">
              <Eyebrow data-id="element-7235">Paid</Eyebrow>
              <p className="font-serif text-3xl text-amber-400 mt-2" data-id="element-7236">
                ${paid.toLocaleString()}
              </p>
            </div>
            <div data-id="element-7237">
              <Eyebrow data-id="element-7238">Remaining</Eyebrow>
              <p className="font-serif text-3xl text-white/60 mt-2" data-id="element-7239">
                ${remaining.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mb-2 flex items-center justify-between text-[10px] tracking-[0.25em] uppercase" data-id="element-7240">
            <span className="text-amber-400/70" data-id="element-7241">Progress</span>
            <span className="text-white/50" data-id="element-7242">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-amber-400/10 overflow-hidden mb-8" data-id="element-7243">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.8
          }} className="h-full bg-gradient-to-r from-amber-400/60 via-amber-400 to-amber-300" data-id="element-7244" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7245">
            {[{
            label: 'Plan',
            value: data.paymentPlan === 'full' ? 'One-Time Payment' : `${data.installments} Installments`
          }, {
            label: 'Tax Receipt',
            value: data.taxReceipt === 'yes' ? 'Routing to Sikh Heritage Museum' : 'Not requested'
          }, {
            label: 'Recognition',
            value: data.recognitionPref === 'anonymous' ? 'Anonymous' : data.recognitionPref === 'public' ? 'Public' : data.recognitionPref === 'family' ? 'Family' : data.recognitionPref === 'organization' ? 'Organization' : 'Discuss with team'
          }, {
            label: 'Impact',
            value: data.sponsorshipType === 'full' ? '~50 students access' : data.sponsorshipType === 'shared' ? 'Shared bus support' : 'Flexible support'
          }].map(row => <div key={row.label} className="border border-amber-400/15 bg-black/40 px-4 py-3 flex items-center justify-between" data-id="element-7246">
                <span className="text-amber-400/60 text-[10px] tracking-[0.25em] uppercase" data-id="element-7247">
                  {row.label}
                </span>
                <span className="text-white/75 text-xs font-light text-right" data-id="element-7248">
                  {row.value}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// IMPACT BLOCK
// =====================================================================
const ImpactBlock = () => <section className="relative py-24 md:py-28 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-7249">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_70%)] pointer-events-none" data-id="element-7250" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-7251">
      <div className="max-w-4xl mx-auto" data-id="element-7252">
        <SectionHeading eyebrow="What Sponsorship Makes Possible" title="Access That" highlight="Otherwise Wouldn't Exist" data-id="element-7253" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12" data-id="element-7254">
          {[{
          icon: GraduationCap,
          body: 'Brings students into an immersive Sikh history experience.'
        }, {
          icon: Building2,
          body: 'Supports school participation across districts.'
        }, {
          icon: Users,
          body: 'Creates access for young people who may not otherwise attend.'
        }, {
          icon: Heart,
          body: 'Connects heritage, identity, and meaningful learning.'
        }].map((it, i) => <motion.div key={it.body} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="border border-amber-400/15 bg-gradient-to-r from-amber-950/10 to-transparent p-5 flex items-start gap-4" data-id="element-7255">
              <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-7256">
                <it.icon className="w-4 h-4 text-amber-400" data-id="element-7257" />
              </div>
              <p className="text-white/75 text-sm sm:text-base font-light leading-relaxed pt-1.5" data-id="element-7258">
                {it.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>
  </section>;
// =====================================================================
// FAQ
// =====================================================================
const FAQ = () => {
  const items = [{
    q: 'What does a bus sponsorship cover?',
    a: 'Each sponsored bus covers round-trip transportation, experience access tickets for approximately 50 students, and school-side coordination.'
  }, {
    q: 'Can I sponsor part of a bus?',
    a: 'Yes. Shared Bus Sponsors pool contributions toward a single bus. We track progress and acknowledge all contributors.'
  }, {
    q: 'Can I pay in installments?',
    a: 'Yes. Installments are processed through Stripe over 3, 6, or 12 months. Your full commitment is confirmed at signup.'
  }, {
    q: 'How do Stripe payments work?',
    a: 'You\u2019re redirected to Stripe\u2019s secure checkout. We never store your card details. You\u2019ll receive a confirmation email from Stripe and from our team.'
  }, {
    q: 'Who issues the tax receipt?',
    a: 'Tax receipts, where applicable, are issued by Sikh Heritage Museum of Canada \u2014 not directly through Sikh Kingdoms.'
  }, {
    q: 'When is the tax receipt provided?',
    a: 'After your details are reviewed and routed to Sikh Heritage Museum of Canada. Timing is subject to the museum\u2019s processing workflow.'
  }, {
    q: 'Can my family or business be recognized?',
    a: 'Yes. You can choose anonymous, family, business, or public acknowledgment \u2014 or discuss custom recognition with the team.'
  }, {
    q: 'Can I sponsor more than one bus?',
    a: 'Absolutely. Check the "discuss a larger sponsorship" option in checkout and our team will reach out to coordinate.'
  }, {
    q: 'Can I speak with someone before paying?',
    a: 'Yes. Use the "Speak With Our Team" button below to arrange a conversation before committing.'
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section className="relative py-24 md:py-28 bg-[#080808] border-t border-amber-400/10" data-id="element-7259">
      <div className="container mx-auto px-4" data-id="element-7260">
        <SectionHeading eyebrow="FAQ" title="Common" highlight="Questions" intro="Everything you might want to know about sponsorship, payment, and tax receipts." data-id="element-7261" />
        <div className="max-w-3xl mx-auto mt-14 space-y-2.5" data-id="element-7262">
          {items.map((it, i) => {
          const open = openIdx === i;
          return <div key={it.q} className={`relative border bg-gradient-to-r from-amber-950/8 to-transparent transition-colors ${open ? 'border-amber-400/40' : 'border-amber-400/15 hover:border-amber-400/30'}`} data-id="element-7263">
                <button type="button" onClick={() => setOpenIdx(open ? null : i)} className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left" data-id="element-7264">
                  <span className="font-serif text-white text-base leading-tight pr-4" data-id="element-7265">
                    {it.q}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-amber-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-7266" />
                </button>
                <AnimatePresence data-id="element-7267">
                  {open && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-7268">
                      <p className="px-5 pb-5 text-white/65 text-sm font-light leading-relaxed" data-id="element-7269">
                        {it.a}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
};
// =====================================================================
// FINAL CTA
// =====================================================================
const FinalCTA = ({
  onSponsorClick
}: {
  onSponsorClick: () => void;
}) => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-7270">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.1),_transparent_60%)] pointer-events-none" data-id="element-7271" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-7272">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto text-center" data-id="element-7273">
        <Eyebrow data-id="element-7274">Sponsor Student Access</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-7275">
          Bring a bus.{' '}
          <span className="text-amber-400 italic" data-id="element-7276">Open a door.</span>
        </h2>
        <div className="w-20 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-7277" />
        <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed mb-10" data-id="element-7278">
          Support meaningful access to Sikh history through a bus sponsorship,
          shared contribution, or structured installment plan.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7279">
          <button onClick={onSponsorClick} className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-7280">
            <Bus className="w-4 h-4" data-id="element-7281" />
            Sponsor a Bus
          </button>
          <a href="mailto:court@lahoretreasury.com?subject=Bus%20Sponsorship%20Inquiry" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-7282">
            <Phone className="w-3.5 h-3.5" data-id="element-7283" />
            Speak With Our Team
          </a>
        </div>
        <p className="mt-8 text-white/35 text-xs font-light flex items-center justify-center gap-2" data-id="element-7284">
          <Mail className="w-3 h-3" data-id="element-7285" />
          court@lahoretreasury.com
        </p>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// PAGE
// =====================================================================
export const SponsorABusPage = () => {
  const [preSelectedType, setPreSelectedType] = useState<SponsorshipType>('');
  const [confirmedData, setConfirmedData] = useState<FormState | null>(null);
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const handleChooseType = (t: SponsorshipType) => {
    setPreSelectedType(t);
    setTimeout(() => scrollTo('checkout'), 100);
  };
  return <LanguageProvider data-id="element-7286">
      <div className="bg-black min-h-screen text-white selection:bg-amber-400/30" data-id="element-7287">
        <ScrollProgress data-id="element-7288" />
        <Navbar data-id="element-7289" />
        <main data-id="element-7290">
          <Hero scrollToCheckout={() => scrollTo('checkout')} scrollToOptions={() => scrollTo('sponsorship-options')} scrollToTax={() => scrollTo('tax-receipt')} data-id="element-7291" />
          <WhatItFunds data-id="element-7292" />
          <SponsorshipOptions onChoose={handleChooseType} data-id="element-7293" />
          <PaymentOptions data-id="element-7294" />
          <TaxReceipt data-id="element-7295" />
          <Checkout initialType={preSelectedType} onConfirmed={s => setConfirmedData(s)} data-id="element-7296" />
          {confirmedData && <SponsorStatus data={confirmedData} data-id="element-7297" />}
          <WhatHappensNext data-id="element-7298" />
          <ImpactBlock data-id="element-7299" />
          <FAQ data-id="element-7300" />
          <FinalCTA onSponsorClick={() => scrollTo('checkout')} data-id="element-7301" />
        </main>
        <Footer data-id="element-7302" />
      </div>
    </LanguageProvider>;
};